import { Component, OnInit, inject, isDevMode, signal } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '@shared/services/auth.service';
import { ThemeService } from '@shared/services/theme.service';
import { LoadingPageComponent } from '@shared/components/loading-page/loading-page.component';
import { ModalStackService } from '@shared/services/modal/modal-stack.service';
import { BRANDING_CONFIG } from '@models/config/branding.config';
import { loadLazyStyle } from '@shared/services/lazy-style.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterModule,
    LoadingPageComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  title = BRANDING_CONFIG.appName;
  isLoading = signal(true);
  showLoadingComponent = signal(true); // Controla si el componente está en el DOM

  private authService = inject(AuthService);
  private router = inject(Router);
  private modalStack = inject(ModalStackService);
  
  ngOnInit() {
    // Override de paleta de marca: se carga apenas arranca la app (no perezoso) porque
    // el color de marca se necesita desde el primer render (botones primarios, focus ring...).
    void loadLazyStyle('brand-palette');

    // Token recovery is handled by the app initializer (app.config.ts) before this
    // component initializes. No localStorage token hydration needed here.

    // Limpiar overflow del body en cada cambio de ruta como medida de seguridad
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Si no hay modales abiertos pero el body tiene overflow hidden, limpiarlo
      const modalsCount = this.modalStack.getOpenModalsCount();
      if (modalsCount === 0 && document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
    });

    // Funciones de depuración en `window`: solo en desarrollo, nunca en el build de producción.
    if (isDevMode() && typeof window !== 'undefined') {
      const debugWindow = window as Window & {
        modalStackDebug?: () => unknown;
        modalStackReset?: () => void;
      };
      debugWindow.modalStackDebug = () => this.modalStack.getDebugInfo();
      debugWindow.modalStackReset = () => this.modalStack.reset();
    }

    // Oculta el loader en cuanto la página terminó de cargar (sin retrasos artificiales).
    if (document.readyState === 'complete') {
      this.hideLoading();
    } else {
      window.addEventListener('load', () => {
        this.hideLoading();
      });
    }
  }

  /** Duración del fundido de salida del loader (`transition: opacity 0.5s` en loading-page). */
  private static readonly LOADER_FADE_MS = 500;

  private hideLoading() {
    this.isLoading.set(false);
    // Se quita del DOM al terminar el fundido (no es espera artificial: es la animación de salida).
    setTimeout(() => this.showLoadingComponent.set(false), AppComponent.LOADER_FADE_MS);
  }
}
